import type { TooltipCoords, TooltipPosition } from "./types";
import { TOOLTIP_OFFSET, TOOLTIP_HORIZONTAL_OFFSET, VIEWPORT_PADDING } from "./constants";

interface CalculatePositionParams {
    triggerRect: DOMRect;
    tooltipRect: DOMRect;
    position: TooltipPosition;
    viewportWidth: number;
    viewportHeight: number;
}

interface CalculatePositionResult {
    coords: TooltipCoords;
    actualPosition: TooltipPosition;
}

/**
 * Calculate coordinates for a specific position
 */
function calculateCoordsForPosition(
    pos: TooltipPosition,
    triggerRect: DOMRect,
    tooltipRect: DOMRect
): TooltipCoords {
    let top = 0;
    let left = 0;

    const isTop = pos.startsWith("top");
    const isBottom = pos.startsWith("bottom");

    // Vertical positioning
    if (isTop) {
        top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET;
    } else if (isBottom) {
        top = triggerRect.bottom + TOOLTIP_OFFSET;
    }

    // Horizontal positioning
    if (pos.endsWith("center")) {
        left =
            triggerRect.left +
            triggerRect.width / 2 -
            tooltipRect.width / 2;
    } else if (pos.endsWith("left")) {
        // Offset left by padding so tooltip content aligns with trigger
        left = triggerRect.left - TOOLTIP_HORIZONTAL_OFFSET;
    } else if (pos.endsWith("right")) {
        // Offset right by padding so tooltip content aligns with trigger
        left = triggerRect.right - tooltipRect.width + TOOLTIP_HORIZONTAL_OFFSET;
    }

    return { top, left };
}

/**
 * Calculate tooltip position with collision detection
 */
export function calculateTooltipPosition({
    triggerRect,
    tooltipRect,
    position,
    viewportWidth,
    viewportHeight,
}: CalculatePositionParams): CalculatePositionResult {
    let actualPosition = position;
    let { top, left } = calculateCoordsForPosition(
        position,
        triggerRect,
        tooltipRect
    );

    // Collision detection - flip vertical position if needed
    if (position.startsWith("top") && top < VIEWPORT_PADDING) {
        actualPosition = position.replace("top", "bottom") as TooltipPosition;
        const flippedCoords = calculateCoordsForPosition(
            actualPosition,
            triggerRect,
            tooltipRect
        );
        top = flippedCoords.top;
    } else if (
        position.startsWith("bottom") &&
        top + tooltipRect.height > viewportHeight - VIEWPORT_PADDING
    ) {
        actualPosition = position.replace("bottom", "top") as TooltipPosition;
        const flippedCoords = calculateCoordsForPosition(
            actualPosition,
            triggerRect,
            tooltipRect
        );
        top = flippedCoords.top;
    }

    // Horizontal collision detection - keep tooltip within viewport
    if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
    } else if (left + tooltipRect.width > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - tooltipRect.width - VIEWPORT_PADDING;
    }

    return {
        coords: { top, left },
        actualPosition,
    };
}

/** Mobile breakpoint - devices with width <= 991px are considered mobile */
export const MOBILE_BREAKPOINT = 991;

/**
 * Detect if device is mobile based on viewport width
 * Uses breakpoint approach: width <= 991px is mobile
 */
export function detectMobile(): boolean {
    if (typeof window === "undefined") return false;

    return window.innerWidth <= MOBILE_BREAKPOINT;
}